package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.parse.Parser

/**
 * I3：校验形参默认值不得引用当前形参列表中位置靠后的形参。
 *
 * 语义上默认值按从左到右顺序求值：第 i 个形参的默认值只能看到 params[0..i-1]。
 * 若默认值表达式出现 `IdentRef(name)` / `LocalRef(name)` 且 name 是 params[j]（j > i），
 * 则报 [DslError.E617_DEFAULT_REFERENCES_LATER_PARAM]。
 *
 * 注意：
 * - 不会穿透嵌套 lambda / 嵌套局部 fun 的 body，避免把 inner scope 的同名变量误当作当前形参引用；
 *   嵌套结构有自己的形参列表与作用域。
 * - 仅做按名查找，不做类型推断；parser 阶段足够识别出简单违规。
 */
internal object DefaultValueChecker {

    /**
     * 在 parser 阶段直接抛错：命中即 [Parser.fail]，确保错误码透传。
     *
     * @param params  形参名列表
     * @param defaults 与 [params] 等长的默认值列表（null 表示无默认值）
     */
    fun checkAndFail(
        p: Parser,
        params: List<String>,
        defaults: List<ExprAst?>,
    ) {
        val offenders = collectOffenders(params, defaults)
        if (offenders.isNotEmpty()) {
            val first = offenders.first()
            p.fail(DslError.E617_DEFAULT_REFERENCES_LATER_PARAM, first.position, first.name)
        }
    }

    /**
     * 在语义阶段收集所有违规，调用方自行决定如何上报。
     * 目前 parser 一遇到就抛，故该方法主要用于未来 checker pass 复用。
     */
    fun collect(
        diag: DiagnosticCollector,
        params: List<String>,
        defaults: List<ExprAst?>,
    ) {
        collectOffenders(params, defaults).forEach { off ->
            diag.error(DslError.E617_DEFAULT_REFERENCES_LATER_PARAM, off.position, off.name)
        }
    }

    /** 违规点：引用了后续形参的节点名与位置。 */
    private data class Offender(
        val name: String,
        val position: top.wcpe.allincore.dsl.compiler.source.SourcePosition,
    )

    private fun collectOffenders(
        params: List<String>,
        defaults: List<ExprAst?>,
    ): List<Offender> {
        if (params.isEmpty() || defaults.isEmpty()) return emptyList()
        val out = mutableListOf<Offender>()
        val size = minOf(params.size, defaults.size)
        for (i in 0 until size) {
            val def = defaults[i] ?: continue
            // 仅 params[i+1..] 视为"后续形参"
            val later = params.subList(i + 1, params.size).toSet()
            if (later.isEmpty()) continue
            walkExpr(def, later, out)
        }
        return out
    }

    private fun walkExpr(e: ExprAst, later: Set<String>, out: MutableList<Offender>) {
        when (e) {
            is ExprAst.IdentRef -> if (e.name in later) out += Offender(e.name, e.position)
            is ExprAst.LocalRef -> if (e.name in later) out += Offender(e.name, e.position)
            is ExprAst.Interp -> e.parts.forEach { part ->
                if (part is ExprAst.Interp.InterpPart.ExprPart) walkExpr(part.expr, later, out)
            }
            is ExprAst.BinaryOp -> { walkExpr(e.left, later, out); walkExpr(e.right, later, out) }
            is ExprAst.UnaryOp -> walkExpr(e.operand, later, out)
            is ExprAst.MemberAccess -> walkExpr(e.target, later, out)
            is ExprAst.SafeMemberAccess -> walkExpr(e.target, later, out)
            // M10 Stage 6：`target!!` 透传 target
            is ExprAst.NonNullAssert -> walkExpr(e.target, later, out)
            is ExprAst.MemberCall -> {
                walkExpr(e.target, later, out); e.args.forEach { walkExpr(it, later, out) }
                e.namedArgs.forEach { walkExpr(it.value, later, out) }
            }
            is ExprAst.SafeMemberCall -> {
                walkExpr(e.target, later, out); e.args.forEach { walkExpr(it, later, out) }
            }
            is ExprAst.Elvis -> { walkExpr(e.left, later, out); walkExpr(e.right, later, out) }
            is ExprAst.Ternary -> {
                walkExpr(e.cond, later, out); walkExpr(e.thenExpr, later, out); walkExpr(e.elseExpr, later, out)
            }
            is ExprAst.IfExpr -> {
                walkExpr(e.cond, later, out)
                e.thenBody.forEach { walkStmt(it, later, out) }
                e.elseBody.forEach { walkStmt(it, later, out) }
            }
            is ExprAst.ListLiteralExpr -> e.elements.forEach { walkExpr(it, later, out) }
            is ExprAst.MapLiteralExpr -> e.entries.forEach { walkExpr(it.value, later, out) }
            is ExprAst.RangeExpr -> { walkExpr(e.start, later, out); walkExpr(e.endValue, later, out) }
            is ExprAst.IndexExpr -> { walkExpr(e.target, later, out); walkExpr(e.index, later, out) }
            is ExprAst.CallExpr -> {
                walkExpr(e.callee, later, out)
                e.args.forEach { walkExpr(it, later, out) }
                e.namedArgs.forEach { walkExpr(it.value, later, out) }
            }
            is ExprAst.BuiltinCallExpr -> e.args.forEach { walkExpr(it, later, out) }
            is ExprAst.NetSubscribeCall -> walkExpr(e.handler, later, out)
            is ExprAst.NetRequestCall -> {
                e.payload.forEach { walkExpr(it.value, later, out) }
                e.callback?.let { walkExpr(it, later, out) }
                e.timeoutMs?.let { walkExpr(it, later, out) }
            }
            is ExprAst.AwaitExpr -> {
                e.netRequestCall?.let { walkExpr(it, later, out) }
                e.suspendCall?.let { walkExpr(it, later, out) }
                e.tailExpr?.let { walkExpr(it, later, out) }
            }
            is ExprAst.WhenExpr -> {
                walkExpr(e.subject, later, out)
                e.branches.forEach { br ->
                    br.values.forEach { walkExpr(it, later, out) }
                    br.body.forEach { walkStmt(it, later, out) }
                }
                e.elseBody?.forEach { walkStmt(it, later, out) }
            }
            is ExprAst.IsExpr -> walkExpr(e.target, later, out)
            // lambda 有自己的形参作用域，内部同名引用不是对当前 fun 形参的引用；跳过
            is ExprAst.LambdaExpr -> Unit
            // 叶子 / 不含子表达式的节点
            is ExprAst.Literal,
            is ExprAst.NullLit,
            is ExprAst.StateRef, is ExprAst.SessionRef, is ExprAst.PersistRef, is ExprAst.DerivedRef,
            is ExprAst.PropsRef,
            is ExprAst.ThemeRef,
            is ExprAst.FunRefExpr -> Unit
        }
    }

    private fun walkStmt(s: StmtAst, later: Set<String>, out: MutableList<Offender>) {
        when (s) {
            is StmtAst.AssignStmt -> walkExpr(s.rhs, later, out)
            is StmtAst.ExprStmt -> walkExpr(s.call, later, out)
            is StmtAst.VarDeclStmt -> walkExpr(s.initializer, later, out)
            is StmtAst.IfStmt -> {
                walkExpr(s.cond, later, out)
                s.thenBody.forEach { walkStmt(it, later, out) }
                s.elseBody.forEach { walkStmt(it, later, out) }
            }
            is StmtAst.WhileStmt -> {
                walkExpr(s.cond, later, out)
                s.body.forEach { walkStmt(it, later, out) }
            }
            is StmtAst.ReturnStmt -> s.value?.let { walkExpr(it, later, out) }
            is StmtAst.TryStmt -> {
                s.tryBody.forEach { walkStmt(it, later, out) }
                s.catchBody.forEach { walkStmt(it, later, out) }
                s.finallyBody.forEach { walkStmt(it, later, out) }
            }
            // batch 语句：递归遍历 body（与 TryStmt 的 tryBody 处理一致）
            is StmtAst.BatchStmt -> s.body.forEach { walkStmt(it, later, out) }
            is StmtAst.ForInStmt -> {
                walkExpr(s.iterable, later, out)
                s.body.forEach { walkStmt(it, later, out) }
            }
            is StmtAst.WhenStmt -> walkExpr(s.whenExpr, later, out)
            // 嵌套 fun 有自己的形参作用域，跳过
            is StmtAst.FunDeclStmt,
            is StmtAst.BreakStmt,
            is StmtAst.ContinueStmt -> Unit
        }
    }
}
